import type { CustomField } from '../../lead.popup/types';
import type { Deal } from '../../types';

import { cache } from '../cache';
import { estClient } from './estClient';
import { type LegacyRichUnit, type UnitInterestedLead } from './unitApi';

class DealApi {
  getCustomFields(): Promise<CustomField[]> {
    return cache.get('dealApi.getCustomFields', async () =>
      estClient.get<CustomField[]>('/api/deal/custom_fields'),
    );
  }

  getCancelCustomFields(): Promise<CustomField[]> {
    return cache.get('dealApi.getCancelCustomFields', async () =>
      estClient.get<CustomField[]>('/api/deal/cancel_custom_fields'),
    );
  }

  async create(body: NewDealProps) {
    await estClient.post('/api/deal', {
      body,
    });
  }

  async update(deal: DealUpdateProps) {
    await estClient.put(`/api/deal/${deal.id}`, {
      body: deal,
    });
  }

  async resell({
    dealId,
    newLeadId,
  }: {
    dealId: Deal['id'];
    newLeadId: string;
  }): Promise<void> {
    await estClient.post(`/api/deal/${dealId}/reassign`, {
      body: {
        lead_id: newLeadId,
      },
    });
  }

  async cancel({
    dealId,
    customFields,
  }: {
    dealId: Deal['id'];
    customFields: { [key: string]: string };
  }): Promise<void> {
    await estClient.post(`/api/deal/${dealId}/cancel`, {
      body: {
        custom_fields: customFields,
      },
    });
  }

  get STATUS() {
    return {
      ACTIVE: '1',
      CLOSED: '2',
      CANCELED: '3',
    } as const;
  }
}

export type PaymentType =
  | // by one payment
  '1'
  // fixed price
  | '2'
  // variable price
  | '3';
export type PaymentFreq = '1' | '2' | '3';

type PaymentsByPartsProps = {
  payable?: number;
  first_payment?: number;

  /**
   * @example '2025-12-31'
   */
  first_payment_date?: string;
  freq_payment?: PaymentFreq;
  period_payment?: number;

  /**
   * @example '2025-12-31'
   */
  payments_start?: string;

  /**
   * @example '2025-12-31'
   */
  payments_end?: string;
};

export type NewDealProps = PaymentsByPartsProps & {
  lead_id: UnitInterestedLead['id'];
  unit_id: LegacyRichUnit['unit_id'];
  status_id: LegacyRichUnit['status_id'];
  opportunity_id: number | null;

  custom_fields: {
    [key: string]: string;
  };

  deal_total: number;
  price: number;
  extra: number;
  payment_type: PaymentType;

  /**
   * @deprecated
   */
  punishment?: '1' | '0';

  /**
   * @deprecated
   */
  discount?: '0';
};

export type DealUpdateProps = Omit<Deal, 'deal_total'> & {
  // for backend it's not important which type will it be
  // however frontend will remain more pure in terms of data types
  deal_total: string | number;
};

export const dealApi = new DealApi();
